import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { taskDisplayTitle } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';
import MobilePlanningPage from './MobilePlanningPage';
import { Avatar } from '../components/Logo';
import { ElevateInfluenceCard } from '../components/ElevateInfluenceCard';
import { parseLocalDate } from '../lib/dates';
import { STAGES } from '../constants/users';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, Loader2, X,
  Users as UsersIcon, MessageSquare, AlertTriangle, Eye, CheckSquare, Sparkles,
} from '../components/icons';

// ---------------------------------------------------------------------------
// Event type catalogue (colors match the reference design)
// ---------------------------------------------------------------------------
const EVENT_TYPES = {
  meeting:  { label: 'Meeting',  color: '#2EA3F2', short: 'Meetings'  },
  call:     { label: 'Call',     color: '#A78BFA', short: 'Calls'     },
  deadline: { label: 'Deadline', color: '#FF6B6B', short: 'Deadlines' },
  review:   { label: 'Review',   color: '#F5A623', short: 'Reviews'   },
  task:     { label: 'Task',     color: '#8B5CF6', short: 'Tasks'     },
  content:  { label: 'Content',  color: '#34D399', short: 'Content'   },
};
const ET_ORDER = ['meeting', 'call', 'deadline', 'review', 'task', 'content'];

const DAYS_SHORT_UC = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
// Iter47z — Format in LOCAL timezone (Paris). Using toISOString() shifts the date
// by -1 for late-evening timestamps → tâches du samedi 11 apparaissaient le dimanche 12.
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function eventTypeOf(t) {
  return (t.event_type && EVENT_TYPES[t.event_type]) ? t.event_type : 'task';
}

export default function PlanningPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobilePlanningPage />;
  return <PlanningPageDesktop />;
}

function PlanningPageDesktop() {
  const { user, isFounder, isFounderStrict } = useAuth();
  const nav = useNavigate();
  const [cursor, setCursor] = useState(startOfWeek(new Date()));
  const [view, setView] = useState('team');     // mine | team | custom
  const [customUserId, setCustomUserId] = useState(null); // for view === 'custom'
  const [scale, setScale] = useState('week');   // day | week
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  // Iter 62ai — Calendly events fused into the planning
  const [calendlyEvents, setCalendlyEvents] = useState([]);
  const [selectedCalendly, setSelectedCalendly] = useState(null); // {event, invitees}
  // Iter 62az — Elevate Influence tasks injected into the calendar grid.
  const [elevateTasks, setElevateTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(null);     // date ISO string
  const [dragOver, setDragOver] = useState(null);

  // Iter43 — Click on a video task in the planning → open the video Kanban drawer
  // (redirects to /tasks?open={tid}). For non-video tasks, keep the classic
  // TaskDetailSheet behavior.
  const handleSelectTask = (t) => {
    if (t?.kind === 'video') {
      nav(`/tasks?open=${t.id}`);
    } else {
      setSelected(t);
    }
  };

  const reload = async () => {
    // Iter 62by — Robust parallel reload. Promise.allSettled means every
    // endpoint fires at once (fast) AND a failure on one doesn't crash the others.
    // Previously sequential try/catch was slow; before that a single failing
    // Promise.all rejected the whole reload — hence the "reload 3 times" bug.
    setLoading(true);
    const [tRes, uRes, pRes] = await Promise.allSettled([
      api.get('/tasks'),
      api.get('/users'),
      api.get('/projects'),
    ]);
    const projectsData = pRes.status === 'fulfilled' ? (pRes.value.data || []) : [];
    setProjects(projectsData);
    setUsers(uRes.status === 'fulfilled' ? (uRes.value.data || []) : []);
    if (tRes.status === 'fulfilled') {
      const projMap = {};
      projectsData.forEach((pr) => { projMap[pr.id] = pr; });
      const enriched = (tRes.value.data || [])
        .filter((tk) => tk.source !== 'admin_todo')  // Iter 62cz — admin_todo isolated
        .map((tk) => ({
          ...tk,
          display_title: taskDisplayTitle(tk, projMap),
        }));
      setTasks(enriched);
    } else {
      console.error('[Planning] /tasks failed:', tRes.reason?.message);
      setTasks([]);
    }
    setLoading(false);
    // Iter 62ai / 62cj — Calendly events for EVERY user (each uses their own
    // OAuth connection; founder falls back to shared PAT). Elevate stays
    // founder-strict. Fire after core so slow externals don't delay the calendar.
    api.get('/calendly/events?days_ahead=60')
      .then((r) => setCalendlyEvents(r.data?.events || []))
      .catch(() => setCalendlyEvents([]));
    if (isFounderStrict) {
      api.get('/elevate/tasks')
        .then((r) => setElevateTasks(r.data?.tasks || []))
        .catch(() => setElevateTasks([]));
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  // Iter 62ba — refresh Elevate tasks when a new one is pushed from the toolbar.
  useEffect(() => {
    const onRefresh = async () => {
      if (!isFounderStrict) return;
      try {
        const e = await api.get('/elevate/tasks');
        setElevateTasks(e.data?.tasks || []);
      } catch (_) { /* silent */ }
    };
    window.addEventListener('asa-elevate-refresh', onRefresh);
    return () => window.removeEventListener('asa-elevate-refresh', onRefresh);
  }, [isFounderStrict]);

  const projColor = (pid) => projects.find((p) => p.id === pid)?.color || null;
  // Iter34 — projects-by-id map for title decoration helper
  const projectsById = useMemo(() => {
    const m = {};
    projects.forEach((p) => { m[p.id] = p; });
    return m;
  }, [projects]);

  // Iter31: a video task has two relevant dates — `due_date` (publication / montage
  // deadline) and `derush_deadline` (when the dérusheur must finish phase 1).
  // The PLANNING shows each task at the date relevant to the VIEWER:
  //   • dérusheur of a phase-1 video → derush_deadline
  //   • everyone else → due_date
  const taskDateFor = (t, viewerId) => {
    if (t.kind === 'video' && t.assigned_to_derush === viewerId && !t.derush_uploaded) {
      return t.derush_deadline || t.due_date;
    }
    return t.due_date;
  };

  // Days shown in the grid: 1 (day) or 7 (week)
  const days = useMemo(() => {
    if (scale === 'day') return [cursor];
    return Array.from({ length: 7 }).map((_, i) => addDays(cursor, i));
  }, [cursor, scale]);

  const isInView = (t) => {
    if (t.parent_task_id) return false;
    if (t.archived) return false;
    if (view === 'mine') {
      // Iter31: a monteur sees the video as deadline-montage, a dérusheur sees it as derush-deadline
      const isMine = t.assigned_to === user.id
        || t.assigned_to_derush === user.id
        || t.assigned_to_montage === user.id
        || (t.assignees || []).includes(user.id);
      if (!isMine) return false;
      return Boolean(taskDateFor(t, user.id));
    }
    if (view === 'custom' && customUserId) {
      const isCust = t.assigned_to === customUserId
        || t.assigned_to_derush === customUserId
        || t.assigned_to_montage === customUserId
        || (t.assignees || []).includes(customUserId);
      if (!isCust) return false;
      return Boolean(taskDateFor(t, customUserId));
    }
    // Vue d'équipe: tout (use due_date)
    return Boolean(t.due_date);
  };

  // tasks grouped by ISO day (uses viewer-aware date — dérusheur sees video at derush_deadline)
  const tasksByDay = useMemo(() => {
    const m = {};
    days.forEach((d) => (m[isoDate(d)] = []));
    const viewerId = view === 'mine' ? user.id : (view === 'custom' ? customUserId : null);
    tasks.forEach((t) => {
      if (!isInView(t)) return;
      const dt = viewerId ? taskDateFor(t, viewerId) : t.due_date;
      if (!dt) return;
      const k = dt.slice(0, 10);
      if (m[k]) m[k].push(t);
    });
    Object.keys(m).forEach((k) =>
      m[k].sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))
    );
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, days, view, user.id, customUserId]);

  // Iter 62ai — Calendly events grouped by ISO day (local timezone).
  const calendlyByDay = useMemo(() => {
    const m = {};
    days.forEach((d) => (m[isoDate(d)] = []));
    calendlyEvents.forEach((e) => {
      if (!e.start_time) return;
      const localDate = new Date(e.start_time);
      const k = isoDate(localDate);
      if (m[k]) m[k].push(e);
    });
    Object.keys(m).forEach((k) => m[k].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    return m;
  }, [calendlyEvents, days]);

  // Iter 62az — Elevate tasks grouped by ISO day.
  const elevateByDay = useMemo(() => {
    const m = {};
    days.forEach((d) => (m[isoDate(d)] = []));
    elevateTasks.forEach((t) => {
      if (!t.due_date) return;
      const k = t.due_date.slice(0, 10);
      if (m[k]) m[k].push(t);
    });
    Object.keys(m).forEach((k) => m[k].sort((a, b) => (a.due_time || '00:00').localeCompare(b.due_time || '00:00')));
    return m;
  }, [elevateTasks, days]);

  // Counters for the header strip
  const counters = useMemo(() => {
    let events = 0, calls = 0, deadlines = 0, milestones = 0;
    Object.values(tasksByDay).forEach((arr) =>
      arr.forEach((t) => {
        events += 1;
        const et = eventTypeOf(t);
        if (et === 'call') calls += 1;
        if (et === 'deadline') deadlines += 1;
        if (et === 'review' || et === 'meeting') milestones += 0; // milestones reserved
      })
    );
    // Iter 62aj — count Calendly events as meetings/calls
    Object.values(calendlyByDay).forEach((arr) => {
      events += arr.length;
      calls += arr.length;
    });
    return { events, calls, deadlines, milestones };
  }, [tasksByDay, calendlyByDay]);

  const moveTask = async (taskId, newDate) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t || (t.due_date && t.due_date.slice(0, 10) === newDate)) return;
    const time = t.due_date && t.due_date.length > 10 ? t.due_date.slice(10) : 'T17:00:00';
    setTasks((prev) => prev.map((x) => x.id === taskId ? { ...x, due_date: newDate + time } : x));
    try {
      await api.patch(`/tasks/${taskId}`, { due_date: newDate + time });
      toast.success('Déplacé');
    } catch { toast.error('Erreur'); reload(); }
  };

  const moveCursor = (n) => setCursor(addDays(cursor, n * (scale === 'day' ? 1 : 7)));
  const today = isoDate(new Date());

  // Range label
  const rangeLabel = scale === 'day'
    ? cursor.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
    : `${cursor.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — ${addDays(cursor, 6).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;

  const visibleMembersCount = view === 'team' ? users.length : 1;

  return (
    <div className="page-fade planning-root">
      {/* ============== Header ============== */}
      <div style={{ padding: '24px 28px 6px' }} className="asa-page-header planning-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }} className="planning-header-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 className="asa-display" style={{ fontSize: 'var(--text-3xl)', margin: 0, letterSpacing: '-0.02em' }}>
                Planning
              </h1>
              {isFounder && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', borderRadius: 999,
                  background: view === 'mine' ? 'rgba(245,166,35,0.13)' : 'rgba(46,163,242,0.15)',
                  border: `1.5px solid ${view === 'mine' ? '#F5A62355' : 'rgba(46,163,242,0.35)'}`,
                  color: view === 'mine' ? '#F5A623' : 'var(--pl)',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>
                  {view === 'mine' ? <CheckSquare size={10} /> : <UsersIcon size={10} />}
                  {view === 'mine' ? 'Mon planning' : "Vue d'équipe"}
                </span>
              )}
            </div>
            <p className="asa-subheading" style={{ fontSize: '0.92rem', margin: '6px 0 0' }}>
              Your week awaits, {user.name.split(' ')[0]}. Organize the game.
            </p>
          </div>
          <button
            data-testid="planning-sync-now"
            onClick={() => { reload(); toast.success('Planning rechargé'); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              height: 38, padding: '0 14px', borderRadius: 10, marginRight: 8,
              background: 'rgba(52,211,153,0.12)',
              border: '1.5px solid rgba(52,211,153,0.35)',
              color: '#34D399', cursor: 'pointer',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            }}
            title="Synchroniser le planning"
          >
            <CalendarIcon size={13} /> Synchroniser maintenant
          </button>
          <button
            data-testid="planning-new-event"
            onClick={() => setShowAdd(today)}
            className="asa-btn"
            style={{ height: 38, padding: '0 16px' }}
          >
            <Plus size={14} /> Nouvel événement
          </button>
        </div>

        {/* Stats row + legend */}
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          gap: 26, marginTop: 22,
        }}>
          <StatPill icon={<CalendarIcon size={14} />} count={counters.events} label="events" color="#5BBEFC" />
          <StatPill icon={<UsersIcon size={14} />} count={counters.calls} label="calls" color="#A78BFA" />
          <StatPill icon={<AlertTriangle size={14} />} count={counters.deadlines} label="deadlines" color="#FF6B6B" />
          <StatPill icon={<Sparkles size={14} />} count={counters.milestones} label="milestones" color="#F5A623" />

          <div style={{ flex: 1 }} />

          {/* Legend */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {ET_ORDER.map((k) => {
              const cfg = EVENT_TYPES[k];
              return (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color }} />
                  <span style={{ fontFamily: 'Lexend', fontSize: '0.74rem', color: 'var(--t2)' }}>{cfg.short}</span>
                </span>
              );
            })}
          </div>
        </div>

        {/* Iter 62ay — Carte Elevate Influence en séparé du planning ASA (founders strict) */}
        {isFounderStrict && (
          <div style={{ marginTop: 22 }}>
            <ElevateInfluenceCard />
          </div>
        )}

        {/* View toggles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22 }}>
          <div style={{ flex: 1 }} />
          {isFounder && (
            <>
              <SegToggle
                testidPrefix="planning-view"
                options={[
                  { value: 'mine', label: 'Mon planning', icon: <CheckSquare size={12} /> },
                  { value: 'team', label: "Vue d'équipe", icon: <UsersIcon size={12} /> },
                  { value: 'custom', label: 'Personnalisée', icon: <Eye size={12} /> },
                ]}
                value={view}
                onChange={(v) => {
                  setView(v);
                  if (v === 'custom' && !customUserId) {
                    // pre-select first non-self user
                    const other = users.find((u) => u.id !== user.id);
                    if (other) setCustomUserId(other.id);
                  }
                }}
              />
              {view === 'custom' && (
                <select
                  data-testid="planning-custom-user"
                  value={customUserId || ''}
                  onChange={(e) => setCustomUserId(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                    borderRadius: 10,
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: 'var(--t1)',
                    cursor: 'pointer',
                  }}
                >
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>Vue de {u.name}</option>
                  ))}
                </select>
              )}
            </>
          )}
          <SegToggle
            testidPrefix="planning-scale"
            options={[
              { value: 'day',  label: 'Jour',    icon: <CalendarIcon size={12} /> },
              { value: 'week', label: 'Semaine', icon: <CalendarIcon size={12} /> },
            ]}
            value={scale}
            onChange={setScale}
          />
        </div>

        {/* Range nav */}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 18, gap: 10 }}>
          <button data-testid="planning-prev" onClick={() => moveCursor(-1)} style={iconBtn}>
            <ChevronLeft size={14} color="var(--t2)" />
          </button>
          <span style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: '1.05rem',
            color: 'var(--t1)', textTransform: 'capitalize',
            letterSpacing: '-0.01em',
          }}>{rangeLabel}</span>
          <button data-testid="planning-next" onClick={() => moveCursor(1)} style={iconBtn}>
            <ChevronRight size={14} color="var(--t2)" />
          </button>
          {/* Iter 62by — "Aujourd'hui" quick-jump button */}
          <button
            data-testid="planning-today"
            onClick={() => setCursor(startOfWeek(new Date()))}
            style={{
              padding: '6px 12px', borderRadius: 999,
              background: 'var(--sf2)', border: '1px solid var(--bd)',
              color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
              cursor: 'pointer', marginLeft: 4,
            }}
          >Aujourd&apos;hui</button>

          <div style={{ flex: 1 }} />
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t3)',
          }}>
            <UsersIcon size={12} color="var(--t3)" />
            {visibleMembersCount} membre{visibleMembersCount > 1 ? 's' : ''} visible{visibleMembersCount > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* ============== Calendar grid ============== */}
      <div style={{ padding: '18px 28px 80px' }}>
        {loading ? (
          <div className="shimmer" style={{ height: 320, borderRadius: 14 }} />
        ) : scale === 'day' ? (
          <DayHourView
            day={cursor}
            tasks={tasksByDay[isoDate(cursor)] || []}
            calendlyEvents={calendlyByDay[isoDate(cursor)] || []}
            onSelectCalendly={setSelectedCalendly}
            users={users}
            projColor={projColor}
            onAdd={(hour, minute = 0) => setShowAdd(isoDate(cursor) + 'T' + String(hour).padStart(2, '0') + ':' + String(minute).padStart(2, '0'))}
            onSelect={handleSelectTask}
          />
        ) : (
          <div
            className="planning-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              borderRadius: 14,
              overflow: 'hidden',
              border: '1.5px solid var(--bd)',
              background: 'var(--sf)',
            }}
          >
            {days.map((d, idx) => {
              const k = isoDate(d);
              const dayTasks = tasksByDay[k] || [];
              const isToday = k === today;
              const dayShort = DAYS_SHORT_UC[idx];
              return (
                <DayColumn
                  key={k}
                  k={k}
                  dayShort={dayShort}
                  dayNum={d.getDate()}
                  isToday={isToday}
                  isLast={idx === days.length - 1}
                  tasks={dayTasks}
                  calendlyEvents={calendlyByDay[k] || []}
                  elevateTasks={elevateByDay[k] || []}
                  onSelectCalendly={setSelectedCalendly}
                  users={users}
                  projColor={projColor}
                  dragOver={dragOver === k}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(k); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    const tid = e.dataTransfer.getData('task-id');
                    setDragOver(null);
                    if (tid) moveTask(tid, k);
                  }}
                  onAdd={() => setShowAdd(k)}
                  onSelect={handleSelectTask}
                />
              );
            })}
          </div>
        )}
      </div>

      {selected && (
        <TaskDetailSheet
          task={selected}
          users={users}
          onClose={() => setSelected(null)}
          onChanged={() => { reload(); setSelected(null); }}
        />
      )}
      {selectedCalendly && (
        <CalendlyDetailModal
          event={selectedCalendly}
          onClose={() => setSelectedCalendly(null)}
        />
      )}
      {showAdd && (
        <QuickAddDialog
          date={showAdd}
          users={users}
          onClose={() => setShowAdd(null)}
          onSaved={() => { setShowAdd(null); reload(); }}
        />
      )}

      <style>{`
        .planning-grid > .day-col + .day-col {
          border-left: 1.5px solid var(--bd);
        }
        @media (max-width: 1000px) {
          .planning-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .planning-grid > .day-col {
            border-left: none !important;
            border-right: 1.5px solid var(--bd);
            border-bottom: 1.5px solid var(--bd);
          }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
const iconBtn = {
  width: 30, height: 30, borderRadius: 8,
  background: 'var(--sf)', border: '1.5px solid var(--bd)',
  display: 'grid', placeItems: 'center', cursor: 'pointer',
};

function StatPill({ icon, count, label, color }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{
        width: 26, height: 26, borderRadius: 7,
        background: `${color}1A`,
        border: `1.5px solid ${color}33`,
        color,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>{icon}</span>
      <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.1rem', color: 'var(--t1)' }}>
        {count}
      </span>
      <span style={{ fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t3)' }}>{label}</span>
    </span>
  );
}

function SegToggle({ options, value, onChange, testidPrefix }) {
  return (
    <div style={{
      display: 'inline-flex', padding: 3,
      background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 11,
      gap: 2,
    }}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            data-testid={`${testidPrefix}-${o.value}`}
            onClick={() => onChange(o.value)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 8,
              background: active ? 'var(--p)' : 'transparent',
              color: active ? '#fff' : 'var(--t3)',
              border: 'none', cursor: 'pointer',
              fontFamily: 'Sora', fontWeight: 600, fontSize: '0.78rem',
              transition: 'background 0.15s var(--ease-out)',
            }}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
function DayColumn({
  k, dayShort, dayNum, isToday, isLast,
  tasks, calendlyEvents = [], elevateTasks = [], onSelectCalendly,
  users, dragOver, projColor,
  onDragOver, onDragLeave, onDrop, onAdd, onSelect,
}) {
  const isEmpty = tasks.length === 0 && calendlyEvents.length === 0 && elevateTasks.length === 0;
  return (
    <div
      className="day-col"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        minHeight: 220,
        display: 'flex', flexDirection: 'column',
        background: dragOver ? 'rgba(46,163,242,0.08)' : 'transparent',
        transition: 'background 0.15s',
      }}
      data-testid={`day-${k}`}
    >
      {/* Day header */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 8,
        padding: '14px 14px 10px',
        borderBottom: '1.5px solid var(--bd)',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
            color: 'var(--t3)', letterSpacing: '0.1em',
          }}>{dayShort}</div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: '1.45rem', lineHeight: 1.1,
            color: isToday ? 'var(--pl)' : 'var(--t1)',
            letterSpacing: '-0.02em',
          }}>{dayNum}</div>
        </div>
        <button
          data-testid={`add-${k}`}
          onClick={onAdd}
          title="Ajouter un événement"
          style={{
            width: 24, height: 24, borderRadius: 6,
            background: 'transparent', border: '1px solid var(--bd)',
            display: 'grid', placeItems: 'center', cursor: 'pointer',
            color: 'var(--t3)',
          }}
        >
          <Plus size={12} />
        </button>
      </div>

      {/* Tasks + Calendly zone */}
      <div style={{
        flex: 1, padding: 12,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {/* Iter 62ai — Calendly events shown first as premium gold cards */}
        {calendlyEvents.map((e) => (
          <CalendlyEventCard
            key={e.uri}
            event={e}
            onClick={() => onSelectCalendly && onSelectCalendly(e)}
          />
        ))}
        {/* Iter 62az — Elevate Influence tasks injected inline with blue accent */}
        {elevateTasks.map((t) => (
          <ElevateTaskCard key={t.id} task={t} />
        ))}
        {isEmpty ? (
          <EmptyCell onAdd={onAdd} />
        ) : tasks.map((t) => (
          <EventCard
            key={t.id}
            task={t}
            users={users}
            projColor={projColor(t.project_id)}
            onClick={() => onSelect(t)}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyCell({ onAdd }) {
  return (
    <button
      onClick={onAdd}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 6, padding: '34px 8px', minHeight: 130,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: 'var(--t3)',
      }}
    >
      <CalendarIcon size={18} strokeWidth={1.5} />
      <span style={{ fontFamily: 'Lexend', fontSize: '0.78rem' }}>Ajouter</span>
    </button>
  );
}

// Iter 62az — Elevate Influence task card inside the planning grid.
// Distinct blue accent + "ELEV" badge so it can't be confused with ASA tasks.
function ElevateTaskCard({ task }) {
  const t = task;
  const isDone = t.status === 'done';
  const hour = t.due_time || '';
  const iso = t.due_date ? `${t.due_date}T${t.due_time || '12:00'}:00` : null;
  const isOverdue = iso && new Date(iso) < new Date() && !isDone;
  return (
    <div
      data-testid={`planning-elevate-${t.id}`}
      title={`Elevate · ${t.client_name || ''} — ${t.title}`}
      style={{
        display: 'flex', flexDirection: 'column', gap: 4,
        padding: '9px 11px', borderRadius: 10,
        background:
          'linear-gradient(180deg, rgba(30,95,255,0.10), rgba(30,95,255,0.02)), var(--sf2)',
        border: '1px solid rgba(30,95,255,0.40)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 3px 10px rgba(30,95,255,0.10)',
        position: 'relative', overflow: 'hidden',
        opacity: isDone ? 0.5 : 1,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
          background: '#5BBEFC',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          padding: '1px 5px', borderRadius: 999,
          background: 'rgba(30,95,255,0.20)', color: '#5BBEFC',
          border: '1px solid rgba(30,95,255,0.45)',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>Elev</span>
        {hour && (
          <span style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
            color: 'var(--t2)', fontVariantNumeric: 'tabular-nums',
          }}>{hour}</span>
        )}
        {isOverdue && (
          <span style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
            color: '#FF6B6B', textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>Retard</span>
        )}
      </div>
      <div style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
        color: 'var(--t1)', lineHeight: 1.25,
        textDecoration: isDone ? 'line-through' : 'none',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {t.client_name ? `${t.client_name} · ${t.title}` : t.title}
      </div>
    </div>
  );
}


// Iter 62ai — Calendly event card (fused into the planning). Gold accent, distinct
// from ASA task cards, click → open CalendlyDetailModal.
function CalendlyEventCard({ event, onClick }) {
  const start = event.start_time ? new Date(event.start_time) : null;
  const hour = start ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
  const eventId = event.uri?.split('/').pop() || '';
  // Iter 62aj — Show invitee name inline (source-of-truth for the founders).
  const invitee = event.invitee_name || '';
  // Iter 62ak — Budget tier styling
  const tierMeta = event.budget_tier === 'gros'  ? { color: '#34D399', label: '+800€' }
                 : event.budget_tier === 'moyen' ? { color: '#F5C56A', label: '300-800€' }
                 : event.budget_tier === 'petit' ? { color: '#FBBF24', label: '<300€' }
                 : null;
  return (
    <button
      data-testid={`planning-calendly-${eventId}`}
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: '10px 12px', textAlign: 'left',
        borderRadius: 10,
        background:
          'linear-gradient(180deg, rgba(245,197,106,0.10), rgba(245,197,106,0.02)), var(--sf2)',
        border: '1px solid rgba(245,197,106,0.35)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 12px rgba(245,197,106,0.12)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Left gold accent bar */}
      <span
        aria-hidden
        style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
          background: 'linear-gradient(180deg, #F5C56A, #E5B04D)',
          boxShadow: '0 0 8px rgba(245,197,106,0.55)',
        }}
      />
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
        color: '#F5C56A', letterSpacing: '0.08em', textTransform: 'uppercase',
        paddingLeft: 6,
      }}>
        <CalendarIcon size={10} strokeWidth={2.2} /> {hour}
      </div>
      <div style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
        color: 'var(--t1)', paddingLeft: 6, letterSpacing: '-0.01em',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{event.name || 'RDV Calendly'}</div>
      {/* Iter 62aj — Invitee name badge (name > "1 invité") */}
      {invitee && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          paddingLeft: 6,
          fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
          color: '#5BBEFC',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          <span
            aria-hidden
            style={{
              width: 5, height: 5, borderRadius: 999,
              background: '#5BBEFC',
              boxShadow: '0 0 6px rgba(91,190,252,0.6)',
              flexShrink: 0,
            }}
          />
          {invitee}
          {/* Iter 62ak — Budget tier badge */}
          {tierMeta && (
            <span
              style={{
                marginLeft: 'auto',
                padding: '0.5px 5px', borderRadius: 999,
                background: `${tierMeta.color}22`,
                border: `1px solid ${tierMeta.color}55`,
                color: tierMeta.color,
                fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
                letterSpacing: '0.03em',
              }}
            >{tierMeta.label}</span>
          )}
        </div>
      )}
    </button>
  );
}

// Iter 62ai — Modal détail RDV Calendly (invités, notes, lien Meet, actions).
function CalendlyDetailModal({ event, onClose }) {
  // Iter 62aj — Use already-fetched invitees + Q&A from the events payload
  // (no extra roundtrip). Fallback to explicit fetch only if not attached.
  const [invitees, setInvitees] = useState(event.invitees || []);
  const [loading, setLoading] = useState(!(event.invitees && event.invitees.length));
  const eventId = event.uri?.split('/').pop() || '';

  useEffect(() => {
    if (event.invitees && event.invitees.length) {
      setInvitees(event.invitees);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/calendly/events/${eventId}/invitees`);
        if (!cancelled) setInvitees(r.data?.invitees || []);
      } catch (e) {
        if (!cancelled) setInvitees([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const start = event.start_time ? new Date(event.start_time) : null;
  const end = event.end_time ? new Date(event.end_time) : null;
  const dayLabel = start ? start.toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }) : '';
  const hour = start ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
  const hourEnd = end ? end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
  const joinUrl = event.location?.join_url;
  const isMeet = event.location?.type === 'google_conference';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(3,7,18,0.68)',
        backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="calendly-detail-modal"
        style={{
          width: '100%', maxWidth: 520,
          background: 'var(--sf)',
          borderRadius: 20,
          border: '1px solid rgba(245,197,106,0.35)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(245,197,106,0.10) inset',
          overflow: 'hidden',
          animation: 'scaleIn 0.18s var(--ease-out)',
        }}
      >
        {/* Gold header */}
        <div style={{
          padding: '18px 22px 16px',
          background:
            'linear-gradient(140deg, rgba(245,197,106,0.14) 0%, rgba(229,176,77,0.04) 100%)',
          borderBottom: '1px solid rgba(245,197,106,0.24)',
          display: 'flex', alignItems: 'flex-start', gap: 14,
        }}>
          <span
            aria-hidden
            style={{
              width: 44, height: 44, borderRadius: 12,
              display: 'grid', placeItems: 'center',
              background:
                'linear-gradient(140deg, #F5C56A 0%, rgba(255,255,255,0.15) 50%, #F5C56A 100%)',
              color: '#0B1220',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.25), 0 6px 14px rgba(245,197,106,0.4)',
              flexShrink: 0,
            }}
          >
            <CalendarIcon size={18} strokeWidth={2.4} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
              color: '#F5C56A', letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}>RDV Calendly</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 20,
              color: 'var(--t1)', marginTop: 3, letterSpacing: '-0.01em',
              lineHeight: 1.2,
            }}>{event.name || 'Sans titre'}</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
              color: 'var(--t2)', marginTop: 6, textTransform: 'capitalize',
            }}>{dayLabel}</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
              color: '#F5C56A', marginTop: 3,
            }}>{hour}{hourEnd ? ` — ${hourEnd}` : ''}</div>
          </div>
          <button
            data-testid="calendly-detail-close"
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              display: 'grid', placeItems: 'center',
              background: 'var(--sf2)',
              border: '1px solid var(--bd)',
              color: 'var(--t2)',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', maxHeight: '54vh', overflowY: 'auto' }}>
          {/* Join link */}
          {joinUrl && (
            <a
              href={joinUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="calendly-detail-join"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', marginBottom: 16,
                borderRadius: 12,
                background: isMeet
                  ? 'linear-gradient(140deg, rgba(52,211,153,0.15), rgba(52,211,153,0.04))'
                  : 'linear-gradient(140deg, rgba(91,190,252,0.15), rgba(91,190,252,0.04))',
                border: `1px solid ${isMeet ? 'rgba(52,211,153,0.35)' : 'rgba(91,190,252,0.35)'}`,
                color: isMeet ? '#34D399' : '#5BBEFC',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
                textDecoration: 'none',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" />
              </svg>
              Rejoindre {isMeet ? 'Google Meet' : 'visio'}
              <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.7 }}>
                {isMeet ? 'meet.google.com' : (event.location?.type || 'visio')}
              </span>
            </a>
          )}

          {/* Invitees */}
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
            color: 'var(--t3)', letterSpacing: '0.14em',
            textTransform: 'uppercase', marginBottom: 10,
          }}>Invités</div>

          {loading ? (
            <div className="shimmer" style={{ height: 60, borderRadius: 10 }} />
          ) : invitees.length === 0 ? (
            <div style={{
              padding: '14px 12px',
              fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
              color: 'var(--t3)', textAlign: 'center',
              background: 'var(--sf2)', borderRadius: 10, border: '1px solid var(--bd)',
            }}>Aucun invité</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
              {invitees.map((inv, i) => {
                const qa = inv.questions_and_answers || [];
                const status = inv.status || '';
                return (
                  <div
                    key={inv.uri || i}
                    data-testid={`calendly-invitee-${i}`}
                    style={{
                      borderRadius: 12,
                      background: 'var(--sf2)',
                      border: '1px solid var(--bd)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Invitee header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      borderBottom: qa.length ? '1px solid var(--bd)' : 'none',
                    }}>
                      <span
                        aria-hidden
                        style={{
                          width: 40, height: 40, borderRadius: 12,
                          display: 'grid', placeItems: 'center',
                          background:
                            'linear-gradient(140deg, #5BBEFC 0%, rgba(255,255,255,0.15) 50%, #5BBEFC 100%)',
                          color: '#0B1220',
                          fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
                          boxShadow:
                            'inset 0 1px 0 rgba(255,255,255,0.55), 0 4px 10px rgba(91,190,252,0.35)',
                          flexShrink: 0,
                        }}
                      >{(inv.name || inv.email || '?')[0].toUpperCase()}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
                          color: 'var(--t1)', letterSpacing: '-0.01em',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{inv.name || inv.email}</div>
                        <div style={{
                          fontFamily: 'Sora', fontWeight: 500, fontSize: 11,
                          color: 'var(--t3)', marginTop: 2,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{inv.email}</div>
                      </div>
                      {status && (
                        <span
                          style={{
                            padding: '3px 8px', borderRadius: 999,
                            background: status === 'canceled' ? 'rgba(255,107,107,0.15)' : 'rgba(52,211,153,0.15)',
                            color: status === 'canceled' ? '#FF6B6B' : '#34D399',
                            fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            flexShrink: 0,
                          }}
                        >{status === 'canceled' ? 'Annulé' : 'Actif'}</span>
                      )}
                    </div>

                    {/* Iter 62aj — Questions & Answers (budget, phone, presentation, etc) */}
                    {qa.length > 0 && (
                      <div
                        data-testid={`calendly-invitee-qa-${i}`}
                        style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}
                      >
                        {qa.map((row, qi) => {
                          const answer = (row.answer || '').trim();
                          if (!answer) return null;
                          const looksLikeBudget = /budget/i.test(row.question || '');
                          const looksLikePhone = /téléphone|phone|whatsapp/i.test(row.question || '');
                          const looksLikeMoney = /€|budget|prix|montant/i.test(answer);
                          const accent = looksLikeBudget || looksLikeMoney ? '#34D399'
                            : looksLikePhone ? '#5BBEFC' : '#F5C56A';
                          return (
                            <div key={qi} style={{
                              padding: '8px 10px',
                              borderRadius: 9,
                              background: 'var(--sf)',
                              border: `1px solid ${accent}22`,
                              borderLeft: `2px solid ${accent}`,
                            }}>
                              <div style={{
                                fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
                                color: accent, letterSpacing: '0.06em',
                                textTransform: 'uppercase', marginBottom: 3,
                              }}>{row.question}</div>
                              <div style={{
                                fontFamily: 'Lexend', fontWeight: 500, fontSize: 12.5,
                                color: 'var(--t1)', lineHeight: 1.45,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                              }}>{answer}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Meeting notes */}
          {event.meeting_notes_plain && (
            <>
              <div style={{
                fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
                color: 'var(--t3)', letterSpacing: '0.14em',
                textTransform: 'uppercase', marginBottom: 8,
              }}>Notes</div>
              <div style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: 'var(--sf2)',
                border: '1px solid var(--bd)',
                fontFamily: 'Lexend', fontSize: 12.5,
                color: 'var(--t2)', lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
              }}>{event.meeting_notes_plain}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EventCard({ task, users, projColor, onClick }) {
  const et = eventTypeOf(task);
  const cfg = EVENT_TYPES[et];
  const finalColor = projColor || cfg.color;
  const isDone = task.stage === 'done';
  const isFinalized = !!task.monteur_finalized;
  const assignees = (task.assignees && task.assignees.length
    ? task.assignees
    : task.assigned_to ? [task.assigned_to] : []
  ).map((id) => users.find((u) => u.id === id)).filter(Boolean);

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('task-id', task.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onClick}
      data-testid={`event-${task.id}`}
      className="event-card"
      style={{
        position: 'relative', cursor: 'grab',
        padding: '7px 9px 7px',
        borderRadius: 7,
        background: `${finalColor}14`,
        border: `1px solid ${finalColor}3D`,
        display: 'flex', flexDirection: 'column', gap: 2,
        transition: 'transform 0.08s var(--ease-out), box-shadow 0.15s, opacity 0.2s',
        borderLeft: `3px solid ${finalColor}`,
        opacity: isDone ? 0.45 : (isFinalized ? 0.75 : 1),
      }}
    >
      <div style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: '0.53rem',
        color: finalColor, letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}>{cfg.label}{isDone && ' · ✓ Terminé'}</div>

      <div style={{
        fontFamily: 'Sora', fontWeight: 600, fontSize: '0.72rem',
        color: 'var(--t1)', letterSpacing: '-0.005em', lineHeight: 1.3,
        overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        textDecoration: isDone ? 'line-through' : 'none',
        textDecorationColor: isDone ? finalColor : undefined,
        textDecorationThickness: isDone ? 1.5 : undefined,
      }}>{task.display_title || task.title}</div>

      {task.description && (
        <div style={{
          fontFamily: 'Lexend', fontSize: '0.62rem',
          color: 'var(--t3)', lineHeight: 1.35,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
          textDecoration: isDone ? 'line-through' : 'none',
        }}>{task.description}</div>
      )}

      {assignees.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          <AvatarStack users={assignees} size={14} />
        </div>
      )}
    </div>
  );
}

function AvatarStack({ users, max = 3, size = 16 }) {
  const list = users.slice(0, max);
  return (
    <div style={{ display: 'inline-flex' }}>
      {list.map((u, i) => (
        <span key={u.id} style={{ marginLeft: i === 0 ? 0 : -5, border: '2px solid var(--sf)', borderRadius: '50%', display: 'inline-flex' }}>
          <Avatar user={u} size={size} />
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
function TaskDetailSheet({ task, users, onClose, onChanged }) {
  const et = eventTypeOf(task);
  const cfg = EVENT_TYPES[et];
  const s = STAGES[task.stage] || STAGES.todo;
  const due = task.due_date ? parseLocalDate(task.due_date) : null;
  const [busy, setBusy] = useState(false);
  // Iter37 — inline editable fields (assignee / deadline / event_type)
  const [assigneeId, setAssigneeId] = useState(task.assigned_to || '');
  const [dueDate, setDueDate] = useState(task.due_date ? task.due_date.slice(0, 10) : '');
  const [dueTime, setDueTime] = useState(task.due_date && task.due_date.length >= 16 ? task.due_date.slice(11, 16) : '17:00');
  const [eventType, setEventType] = useState(et);
  const [saving, setSaving] = useState(false);
  // Iter39 — editable title & description (locked for video tasks since title follows "Vidéo N XXX")
  const isVideo = task.kind === 'video';
  const [title, setTitle] = useState(task.title || '');
  const [description, setDescription] = useState(task.description || '');

  const toggleDone = async () => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}`, { stage: 'done' });
      toast.success('Tâche terminée — archivée auto dans 5 min');
      onChanged();
    } catch { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const newDue = dueDate ? `${dueDate}T${dueTime || '17:00'}:00` : null;
      const payload = {
        assigned_to: assigneeId || null,
        due_date: newDue,
        event_type: eventType,
        description,
      };
      // Iter39 — for non-video tasks, allow editing title too
      if (!isVideo) payload.title = title;
      await api.patch(`/tasks/${task.id}`, payload);
      toast.success('Tâche mise à jour');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const hasChanges = (
    (assigneeId || null) !== (task.assigned_to || null)
    || (dueDate ? `${dueDate}T${dueTime || '17:00'}:00` : null) !== (task.due_date || null)
    || eventType !== et
    || description !== (task.description || '')
    || (!isVideo && title !== (task.title || ''))
  );

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(6,11,24,0.7)', backdropFilter: 'blur(8px)',
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <aside
        onClick={(e) => e.stopPropagation()}
        className="page-fade"
        style={{
          width: '100%', maxWidth: 440,
          background: 'var(--sf)', borderLeft: '1.5px solid var(--bd)',
          overflowY: 'auto', padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <span style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
            padding: '4px 10px', borderRadius: 999,
            background: `${cfg.color}1F`, color: cfg.color,
            textTransform: 'uppercase', letterSpacing: '0.07em',
          }}>{cfg.label}</span>
          <span style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
            padding: '4px 10px', borderRadius: 999,
            background: `${s.color}1A`, color: s.color,
            textTransform: 'uppercase', letterSpacing: '0.07em',
          }}>{s.label}</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', padding: 6 }}>
            <X size={18} color="var(--t3)" />
          </button>
        </div>

        {/* Iter39 — editable title (locked for video tasks) */}
        {isVideo ? (
          <h2 className="asa-heading" style={{ margin: '0 0 6px', fontSize: 'var(--text-xl)', color: 'var(--t1)' }}>
            {task.display_title || task.title}
          </h2>
        ) : (
          <input
            data-testid="detail-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre de la tâche"
            style={{
              width: '100%', marginBottom: 12, padding: '8px 11px',
              fontFamily: 'Sora', fontWeight: 800, fontSize: '1.05rem', color: 'var(--t1)',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
            }}
          />
        )}

        {/* Iter39 — editable description */}
        <textarea
          data-testid="detail-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Description (optionnelle)"
          style={{
            width: '100%', marginBottom: 18, padding: '9px 11px',
            fontFamily: 'Lexend', fontSize: '0.86rem', color: 'var(--t2)',
            background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
            resize: 'vertical', lineHeight: 1.55,
          }}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
          {/* Iter37 — Event type (editable) */}
          <div>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Type</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {ET_ORDER.map((k) => {
                const c = EVENT_TYPES[k];
                const active = eventType === k;
                return (
                  <button
                    key={k} type="button"
                    data-testid={`detail-et-${k}`}
                    onClick={() => setEventType(k)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 10px', borderRadius: 999,
                      background: active ? `${c.color}22` : 'var(--sf2)',
                      border: `1.5px solid ${active ? c.color : 'var(--bd)'}`,
                      color: active ? c.color : 'var(--t3)',
                      fontFamily: 'Sora', fontWeight: 600, fontSize: '0.68rem',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Iter37 — Assigné à (editable, founders inclus) */}
          <div>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Assigné à</div>
            <select
              data-testid="detail-assignee-select"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              style={{
                width: '100%', padding: '9px 11px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
                fontFamily: 'Lexend', fontSize: '0.85rem', color: 'var(--t1)',
              }}
            >
              <option value="">— Non assigné —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Iter37 — Échéance (editable) */}
          <div>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>
              Échéance {due && (
                <span style={{ color: 'var(--t3)', fontWeight: 400, marginLeft: 6 }}>
                  · {due.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="date"
                data-testid="detail-due-date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{
                  flex: 1, padding: '9px 11px',
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
                  fontFamily: 'Lexend', fontSize: '0.85rem', color: 'var(--t1)', colorScheme: 'dark',
                }}
              />
              <input
                type="time"
                data-testid="detail-due-time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                style={{
                  width: 100, padding: '9px 11px',
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
                  fontFamily: 'Lexend', fontSize: '0.85rem', color: 'var(--t1)', colorScheme: 'dark',
                }}
              />
            </div>
          </div>
        </div>

        {hasChanges && (
          <button
            data-testid="detail-save-changes"
            onClick={saveChanges} disabled={saving}
            className="asa-btn" style={{ width: '100%', marginBottom: 10 }}
          >
            {saving ? <Loader2 size={15} className="spin" /> : 'Enregistrer les modifications'}
          </button>
        )}

        <button
          data-testid="planning-toggle-done"
          onClick={toggleDone} disabled={busy}
          className="asa-btn-ghost asa-btn" style={{ width: '100%' }}
        >
          {busy ? <Loader2 size={15} className="spin" /> : 'Marquer terminé'}
        </button>
      </aside>
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="asa-eyebrow" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: 'Lexend', fontSize: '0.86rem', color: 'var(--t1)' }}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function QuickAddDialog({ date, users, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventType, setEventType] = useState('task');
  const [time, setTime] = useState('17:00');
  const [assignees, setAssignees] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleAssignee = (id) =>
    setAssignees((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post('/tasks', {
        title: title.trim(),
        description: description.trim(),
        stage: 'todo',
        priority: 'medium',
        event_type: eventType,
        assigned_to: assignees[0] || null,
        assignees,
        due_date: `${date}T${time}:00`,
      });
      toast.success('Événement ajouté');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(6,11,24,0.7)', backdropFilter: 'blur(8px)',
      display: 'grid', placeItems: 'center', padding: 18,
    }}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 480,
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 20, padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)' }}>Nouvel événement</h2>
            <div className="asa-eyebrow" style={{ marginTop: 4, color: 'var(--pl)' }}>
              {new Date(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ marginLeft: 'auto', padding: 6 }}>
            <X size={18} color="var(--t3)" />
          </button>
        </div>

        {/* Event type picker */}
        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Type</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {ET_ORDER.map((k) => {
            const cfg = EVENT_TYPES[k];
            const active = eventType === k;
            return (
              <button
                key={k}
                type="button"
                data-testid={`et-${k}`}
                onClick={() => setEventType(k)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 999,
                  background: active ? `${cfg.color}22` : 'var(--sf2)',
                  border: `1.5px solid ${active ? cfg.color : 'var(--bd)'}`,
                  color: active ? cfg.color : 'var(--t3)',
                  fontFamily: 'Sora', fontWeight: 600, fontSize: '0.72rem',
                  cursor: 'pointer',
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />
                {cfg.label}
              </button>
            );
          })}
        </div>

        <input
          autoFocus
          data-testid="quickadd-title"
          placeholder="Titre de l'événement"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={inputStyle}
        />
        <textarea
          data-testid="quickadd-desc"
          placeholder="Description (optionnel)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
        />

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Heure</div>
            <input
              type="time"
              data-testid="quickadd-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              style={{ ...inputStyle, marginBottom: 0 }}
            />
          </div>
        </div>

        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Membres assignés</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
          {users.map((u) => {
            const active = assignees.includes(u.id);
            return (
              <button
                key={u.id} type="button"
                data-testid={`quickadd-member-${u.id}`}
                onClick={() => toggleAssignee(u.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 11px 5px 5px', borderRadius: 999,
                  background: active ? 'rgba(46,163,242,0.12)' : 'var(--sf2)',
                  border: `1.5px solid ${active ? 'rgba(46,163,242,0.4)' : 'var(--bd)'}`,
                  cursor: 'pointer',
                }}
              >
                <Avatar user={u} size={20} />
                <span style={{ fontFamily: 'Lexend', fontSize: '0.78rem', color: active ? 'var(--t1)' : 'var(--t3)' }}>
                  {u.name.split(' ')[0]}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button
            data-testid="quickadd-submit"
            type="submit"
            disabled={busy || !title.trim()}
            className="asa-btn" style={{ flex: 1.4 }}
          >
            {busy ? <Loader2 size={15} className="spin" /> : 'Ajouter à la semaine'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '11px 13px',
  background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 11,
  fontFamily: 'Lexend', fontSize: '0.9rem', color: 'var(--t1)',
  marginBottom: 12,
};

// ===========================================================================
// DayHourView — vertical hour grid (8h-22h) for the "Jour" scale
// ===========================================================================
// 30-min slots from 8:00 to 22:00 → 28 entries
const HOURS = Array.from({ length: 28 }).map((_, i) => ({
  h: 8 + Math.floor(i / 2),
  m: (i % 2) === 0 ? 0 : 30,
}));

function DayHourView({ day, tasks, calendlyEvents = [], onSelectCalendly, users, projColor, onAdd, onSelect }) {
  const dayLabel = day.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  // Group tasks by HH:MM slot
  const slotKey = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const byHour = {};
  tasks.forEach((t) => {
    const h = t.due_date && t.due_date.length >= 13 ? parseInt(t.due_date.slice(11, 13), 10) : 17;
    const rawM = t.due_date && t.due_date.length >= 16 ? parseInt(t.due_date.slice(14, 16), 10) : 0;
    const m = rawM >= 30 ? 30 : 0;
    const key = slotKey(h, m);
    (byHour[key] = byHour[key] || []).push(t);
  });
  // Iter 62aj — Same bucket, tag by type so calendly cards land at their own hour.
  const calendlyByHour = {};
  calendlyEvents.forEach((e) => {
    if (!e.start_time) return;
    const dt = new Date(e.start_time);
    const h = dt.getHours();
    const rawM = dt.getMinutes();
    const m = rawM >= 30 ? 30 : 0;
    const key = slotKey(h, m);
    (calendlyByHour[key] = calendlyByHour[key] || []).push(e);
  });

  return (
    <div style={{
      background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 14,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px',
        borderBottom: '1.5px solid var(--bd)',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: 'rgba(46,163,242,0.13)',
          border: '1.5px solid rgba(46,163,242,0.3)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <span style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: '1.1rem',
            color: 'var(--pl)', letterSpacing: '-0.02em',
          }}>{day.getDate()}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: '1.05rem',
            color: 'var(--t1)', textTransform: 'capitalize', letterSpacing: '-0.01em',
          }}>{dayLabel}</div>
          <div style={{ fontFamily: 'Lexend', fontSize: '0.74rem', color: 'var(--t3)' }}>
            {tasks.length + calendlyEvents.length} événement{(tasks.length + calendlyEvents.length) > 1 ? 's' : ''} ce jour
          </div>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {HOURS.map(({ h, m }) => {
          const key = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          const items = byHour[key] || [];
          const calendlyItems = calendlyByHour[key] || [];
          const is17 = h === 17 && m === 0;
          const isHalf = m === 30;
          return (
            <div
              key={key}
              data-testid={`hour-${key}`}
              onClick={() => onAdd(h, m)}
              style={{
                display: 'flex', alignItems: 'stretch',
                minHeight: isHalf ? 38 : 44,
                borderBottom: isHalf ? '1px dashed var(--bd)' : '1px solid var(--bd)',
                cursor: 'pointer',
                background: is17 ? 'rgba(255,107,107,0.05)' : 'transparent',
                position: 'relative',
              }}
            >
              {/* Hour label */}
              <div style={{
                width: 64, padding: '6px 10px',
                fontFamily: 'Sora', fontWeight: 700, fontSize: '0.68rem',
                color: is17 ? '#FF6B6B' : isHalf ? 'var(--t3)' : 'var(--t2)',
                borderRight: '1px solid var(--bd)',
                flexShrink: 0,
                opacity: isHalf ? 0.6 : 1,
              }}>
                {key}
                {is17 && (
                  <div style={{
                    fontFamily: 'Lexend', fontSize: '0.5rem',
                    color: '#FF6B6B', marginTop: 1, fontWeight: 400,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>Deadlines</div>
                )}
              </div>

              {/* Events placed at this slot */}
              <div style={{ flex: 1, padding: '4px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Iter 62aj — Calendly events show FIRST (gold accent) */}
                {calendlyItems.map((e) => (
                  <div
                    key={e.uri}
                    onClick={(ev) => { ev.stopPropagation(); onSelectCalendly && onSelectCalendly(e); }}
                  >
                    <CalendlyEventCard event={e} onClick={() => onSelectCalendly && onSelectCalendly(e)} />
                  </div>
                ))}
                {items.map((t) => (
                  <DayEventCard key={t.id} task={t} users={users} projColor={projColor(t.project_id)} onClick={() => onSelect(t)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayEventCard({ task, users, projColor, onClick }) {
  const et = eventTypeOf(task);
  const cfg = EVENT_TYPES[et];
  const finalColor = projColor || cfg.color;
  const assignees = (task.assignees && task.assignees.length
    ? task.assignees
    : task.assigned_to ? [task.assigned_to] : []
  ).map((id) => users.find((u) => u.id === id)).filter(Boolean);
  const time = task.due_date && task.due_date.length >= 16 ? task.due_date.slice(11, 16) : '';

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={`day-event-${task.id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '6px 10px',
        background: `${finalColor}14`,
        borderLeft: `3px solid ${finalColor}`,
        borderRadius: 6,
        cursor: 'pointer',
      }}
    >
      <span style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
        color: finalColor, padding: '2px 7px', borderRadius: 999,
        background: `${finalColor}22`,
        textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0,
      }}>{cfg.label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 600, fontSize: '0.82rem',
          color: 'var(--t1)', lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{task.display_title || task.title}</div>
        {task.description && (
          <div style={{
            fontFamily: 'Lexend', fontSize: '0.68rem', color: 'var(--t3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{task.description}</div>
        )}
      </div>
      {time && (
        <span style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: '0.7rem', color: 'var(--t3)' }}>
          {time}
        </span>
      )}
      {assignees.length > 0 && <AvatarStack users={assignees} size={18} />}
    </div>
  );
}

